import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, getAnalysisStatus, triggerAnalysis } from '../lib/api.js';
import type { AnalysisDto, AnalysisStructured } from '../types.js';
import { AiProvenance } from './AiProvenance.js';

interface Props {
  threadId: string;
  analysis: AnalysisDto | null;
}

type Feedback = { level: 'success' | 'error'; message: string } | null;
/**
 * Local tracking state for "we kicked off an analysis and are now
 * waiting for it to finish." Holds:
 *   - `previousId`: the analysis_id of the row that was on this thread
 *     when we kicked off. Compared against the new poll's analysis_id
 *     to distinguish "new analysis landed" from "terminal state with
 *     no new result" (cache hit, vanished job, etc).
 *   - `force`: which mode we requested. Affects the message we show
 *     when previousId === incoming analysis_id (force=true expected a
 *     new row; force=false is fine with the cached result).
 *   - `deadlineMs`: the wall-clock deadline (60s) so the UI can
 *     surface a "taking longer than expected" message if polling never
 *     reaches a terminal state.
 */
type QueuedAnalysis = {
  previousId: string | null;
  force: boolean;
  deadlineMs: number;
};

const URGENCY_COLORS: Record<AnalysisStructured['urgency'], string> = {
  low: 'text-zinc-400',
  medium: 'text-amber-300',
  high: 'text-orange-300',
  critical: 'text-red-300',
};

const RISK_COLORS: Record<AnalysisStructured['risk_level'], string> = {
  low: 'text-zinc-400',
  medium: 'text-amber-300',
  high: 'text-red-300',
};

const ACTION_LABELS: Record<AnalysisStructured['suggested_action'], string> = {
  reply: 'Reply',
  defer: 'Defer',
  mark_no_response_needed: 'No response needed',
  needs_human_judgment: 'Needs human judgment',
};

const ANALYSIS_TIMEOUT_MS = 60_000;
const STATUS_POLL_MS = 2_000;

/**
 * B3.7 (#2): client-side fallback for the auto-analysis throttle
 * window when we haven't yet polled the status endpoint. Mirrors the
 * worker default in `packages/config/src/index.ts`
 * (AUTO_ANALYSIS_MIN_INTERVAL_MINUTES). If prod overrides the env to
 * a different value, the polled status endpoint's authoritative field
 * (`auto_throttle_window_ends_at`) wins as soon as it's fetched. The
 * fallback drift is at most a few minutes of UI text — the operator
 * Analyze button bypasses the throttle anyway, so this is hint copy,
 * not enforcement.
 */
const DEFAULT_THROTTLE_MINUTES = 15;

/**
 * Intelligence panel — collapsed by default so the correspondence
 * stays the primary surface.
 *
 * Polling design (batch-1):
 *   - When the operator triggers Analyze/Regenerate, the panel sets
 *     `queuedAnalysis` locally and starts polling the cheap
 *     `/threads/:id/analysis-status` endpoint. It deliberately does
 *     NOT invalidate `['thread', threadId]` on a loop — that used
 *     to refetch the whole thread every 2s and re-fired the
 *     ThreadDetail scroll-to-bottom (B1-H1).
 *   - The Analyze button stays disabled the entire time the panel
 *     is `isAnalyzing` or queued/running, so a fast-clicking
 *     operator can't enqueue duplicate Anthropic work. The backend
 *     also coalesces (see `routes/analysis.ts`), but the frontend
 *     disable is the first line of defence.
 *   - When the poll observes a terminal state (`success`/`failed`)
 *     it invalidates the full thread ONCE so the new analysis lands,
 *     then stops polling.
 *   - If 60s elapse without a terminal state, the panel surfaces
 *     "taking longer than expected" so the operator knows to retry.
 */
export function IntelligencePanel({ threadId, analysis }: Props) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [expanded, setExpanded] = useState(false);
  const [queuedAnalysis, setQueuedAnalysis] = useState<QueuedAnalysis | null>(null);

  const isQueued = queuedAnalysis !== null;

  // Reset expansion + queued state when the thread changes. Critical so
  // a stale queuedAnalysis from a previous thread doesn't keep the
  // Analyze button disabled on the new one. The setExpanded/setFeedback
  // identities are stable so Biome sees them as "unnecessary" deps, but
  // threadId IS the trigger we want.
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger is threadId, not the setters
  useEffect(() => {
    setExpanded(false);
    setFeedback(null);
    setQueuedAnalysis(null);
  }, [threadId]);

  // Cheap status poll — only enabled while we're tracking a queued
  // analysis locally. Stops automatically as soon as `isQueued` flips
  // back to false.
  //
  // We pass our locally-known previous analysis id as
  // `?since_analysis_id=…` so the SERVER returns a
  // `result_relation` ('new' | 'unchanged' | 'none') and we never
  // need a raw analysis id in the response body. The query key
  // includes the since-id so a thread switch (which resets
  // queuedAnalysis) refetches with the new context.
  const sinceAnalysisId = queuedAnalysis?.previousId ?? null;
  const statusQuery = useQuery({
    queryKey: ['analysis-status', threadId, sinceAnalysisId],
    queryFn: ({ signal }) =>
      getAnalysisStatus(threadId, { signal, sinceAnalysisId: sinceAnalysisId ?? undefined }),
    enabled: isQueued,
    refetchInterval: isQueued ? STATUS_POLL_MS : false,
    staleTime: 0,
    gcTime: 0,
  });

  // Terminal-state handler: when the poll sees a terminal state
  // (success / failed / expired), clear local state and invalidate
  // the thread exactly ONCE so the new analysis shows up. The
  // distinguishing logic — "did the worker actually produce a new
  // row?" — runs here so the operator never sees an old success
  // misreported as the fresh one they just requested.
  useEffect(() => {
    if (!isQueued || !queuedAnalysis || !statusQuery.data) return;
    const { state, error_code, result_relation } = statusQuery.data;
    if (state !== 'success' && state !== 'failed') return;

    setQueuedAnalysis(null);
    void queryClient.invalidateQueries({ queryKey: ['thread', threadId] });

    if (state === 'failed') {
      setFeedback({
        level: 'error',
        message: `Analysis failed${error_code ? ` (${error_code})` : ''}. Try Regenerate.`,
      });
      return;
    }

    // state === 'success' — backend's `result_relation` is the
    // authoritative answer for "did a new analysis land?" The client
    // no longer compares raw analysis ids; this lives server-side
    // (see B1.2-2).
    if (result_relation === 'new') {
      setFeedback({ level: 'success', message: 'Analysis ready.' });
    } else if (queuedAnalysis.force) {
      // We explicitly asked for a regenerate but no new row was
      // produced. Most likely the worker terminated without writing
      // (rare; recoverable by trying again).
      setFeedback({
        level: 'error',
        message: 'No new analysis was produced. Try Regenerate.',
      });
    } else {
      // Cache hit on the fingerprint — the existing analysis IS the
      // answer for this thread + prompt + model combo. Tell the
      // operator it was reused so they can hit Regenerate if they
      // want fresh output.
      setFeedback({
        level: 'success',
        message: 'Analysis result unchanged (cache hit). Use Regenerate for a fresh run.',
      });
    }
  }, [isQueued, queuedAnalysis, statusQuery.data, queryClient, threadId]);

  // 60s deadline: if the poll never reaches a terminal state, surface a
  // "taking longer than expected" message and stop polling.
  useEffect(() => {
    if (!queuedAnalysis) return;
    const remaining = queuedAnalysis.deadlineMs - Date.now();
    if (remaining <= 0) {
      setQueuedAnalysis(null);
      setFeedback({
        level: 'error',
        message: 'Analysis is taking longer than expected. Refresh or try Regenerate.',
      });
      return;
    }
    const timeout = window.setTimeout(() => {
      setQueuedAnalysis(null);
      setFeedback({
        level: 'error',
        message: 'Analysis is taking longer than expected. Refresh or try Regenerate.',
      });
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [queuedAnalysis]);

  const analyzeMutation = useMutation({
    mutationFn: (force: boolean) =>
      triggerAnalysis(threadId, { force }).then((response) => ({ ...response, force })),
    onSuccess: (response) => {
      // Whether the backend enqueued or coalesced onto an existing job,
      // both outcomes mean "wait for the worker." Start the local
      // status poll either way.
      setQueuedAnalysis({
        previousId: analysis?.id ?? null,
        force: response.force,
        deadlineMs: Date.now() + ANALYSIS_TIMEOUT_MS,
      });
      setFeedback({
        level: 'success',
        message: response.coalesced
          ? 'Analysis already in progress — waiting for the worker.'
          : 'Analysis queued. Result will appear when the worker finishes.',
      });
    },
    onError: (err) => setFeedback({ level: 'error', message: explain(err) }),
  });

  const isAnalyzing = analyzeMutation.isPending;
  const isRegenerate = analysis !== null;
  const structured = analysis?.structured_output ?? null;
  const analysisFailed = analysis?.status === 'failed';

  // B3.7 (#2): pick the best available "when does the auto-throttle
  // window end?" signal. The polled status endpoint is server-
  // authoritative; before we've polled (or when the operator hasn't
  // clicked Analyze yet), fall back to client-side arithmetic on the
  // initial analysis prop. Either way we render the same notice — the
  // operator sees one consistent string.
  const throttleEndsAtIso = computeThrottleEndIso(
    statusQuery.data?.auto_throttle_window_ends_at ?? null,
    analysis,
  );
  const throttleMinutesLeft = throttleEndsAtIso ? minutesUntil(throttleEndsAtIso) : 0;
  const isThrottled = throttleMinutesLeft > 0;
  // Disable the button while the request is in flight OR while we're
  // already waiting for an enqueued job to finish. Without this, repeat
  // clicks land repeat POSTs (the backend would coalesce, but a noisy
  // dashboard makes operators distrust the system).
  const buttonDisabled = isAnalyzing || isQueued;
  const buttonLabel = isAnalyzing
    ? 'Queuing…'
    : isQueued
      ? 'Analyzing…'
      : isRegenerate
        ? 'Regenerate'
        : 'Analyze';

  return (
    <div className="border-t border-zinc-800 bg-zinc-950" data-testid="intelligence-panel">
      {/* Compact strip — always rendered. The Expand toggle reveals the
          detail block below it. */}
      <div className="u-conv-pad-sm flex items-center gap-2 px-4 py-1.5 text-[11px]">
        <button
          type="button"
          onClick={() => {
            setFeedback(null);
            analyzeMutation.mutate(isRegenerate);
          }}
          disabled={buttonDisabled}
          data-testid="intelligence-analyze"
          className="rounded border border-sky-900 bg-sky-950 px-2 py-0.5 text-[11px] font-medium text-sky-100 hover:bg-sky-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {buttonLabel}
        </button>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="intelligence-detail"
          data-testid="intelligence-expand"
          className="inline-flex items-center gap-1 rounded text-[10px] font-semibold uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
          title={expanded ? 'Collapse intelligence' : 'Expand intelligence'}
        >
          <Chevron expanded={expanded} />
          Intelligence
        </button>

        {structured ? (
          <>
            <span
              data-testid="suggested-action"
              className="rounded bg-zinc-900 px-1.5 py-0.5 text-zinc-200 ring-1 ring-inset ring-zinc-800"
            >
              {ACTION_LABELS[structured.suggested_action]}
            </span>
            <span
              data-testid="urgency"
              className={`font-medium uppercase ${URGENCY_COLORS[structured.urgency]}`}
            >
              {structured.urgency}
            </span>
            <span
              data-testid="risk-level"
              className={`uppercase ${RISK_COLORS[structured.risk_level]}`}
            >
              risk {structured.risk_level}
            </span>
            {analysis ? <AiProvenance analysis={analysis} className="truncate" /> : null}
          </>
        ) : analysisFailed ? (
          <span className="font-medium text-red-300" data-testid="intelligence-failed">
            Analysis failed{analysis?.error_code ? ` (${analysis.error_code})` : ''}
          </span>
        ) : (
          <span className="italic text-zinc-500" data-testid="intelligence-empty">
            No analysis yet
          </span>
        )}
      </div>

      {feedback ? (
        <div
          role={feedback.level === 'error' ? 'alert' : 'status'}
          className={`px-4 pb-1.5 text-[11px] ${
            feedback.level === 'error' ? 'text-red-300' : 'text-emerald-300'
          }`}
        >
          {feedback.message}
        </div>
      ) : null}

      {isThrottled ? (
        <div
          data-testid="intelligence-throttle-notice"
          className="px-4 pb-1.5 text-[10px] italic text-zinc-500"
          title="Webhook-driven auto-analysis is paused; the Analyze button bypasses this throttle."
        >
          Auto-analysis paused for ~{throttleMinutesLeft} min — click Analyze to bypass.
        </div>
      ) : null}

      {expanded ? (
        <div
          id="intelligence-detail"
          className="border-t border-zinc-800 px-4 py-3"
          data-testid="intelligence-detail"
        >
          {structured ? (
            <AnalysisDetails structured={structured} />
          ) : analysisFailed ? (
            <div className="text-xs text-red-300">
              Analysis failed{analysis?.error_code ? ` (${analysis.error_code})` : ''}. Click
              Regenerate to retry.
            </div>
          ) : (
            <div className="text-xs italic text-zinc-500">
              No analysis yet. Click Analyze to generate one.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 80ms ease' }}
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

function AnalysisDetails({ structured }: { structured: AnalysisStructured }) {
  const confidencePct = Math.round(structured.confidence * 100);
  return (
    <div className="space-y-3 text-xs" data-testid="intelligence-reasoning">
      {/* The reasoning only. The proposed reply itself is the editable field
          below the panel — not duplicated here. */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <span className="mr-1 text-zinc-500">Confidence</span>
          <span className="font-medium text-zinc-200">{confidencePct}%</span>
        </div>
        <div className="text-zinc-500">
          {structured.requires_response ? 'response expected' : 'no response needed'}
        </div>
      </div>

      <Field label="Ask summary" value={structured.ask_summary} />
      <Field label="Why" value={structured.explanation} />
      {structured.relationship_context ? (
        <Field label="Relationship" value={structured.relationship_context} />
      ) : null}

      {structured.open_loops.length > 0 ? (
        <ListField label="Open loops" items={structured.open_loops} />
      ) : null}
      {structured.relevant_context.length > 0 ? (
        <ListField label="Relevant context" items={structured.relevant_context} />
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className="text-zinc-200">{value}</div>
    </div>
  );
}

function ListField({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <ul className="mt-0.5 list-disc pl-4 text-zinc-200">
        {items.map((it) => (
          // Items are short distinct strings (open loops / context snippets).
          // Using the value itself as the key is stable enough here.
          <li key={`${label}::${it}`}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * B3.7 (#2): pick the best throttle-window-end ISO string we have.
 * Prefer the server's authoritative value when the status endpoint
 * has responded; otherwise fall back to client-side arithmetic on
 * the analysis prop (last successful analysis + the documented prod
 * window). Returns null when there's nothing to time-box.
 */
export function computeThrottleEndIso(
  serverField: string | null,
  analysis: AnalysisDto | null,
): string | null {
  if (serverField) return serverField;
  if (!analysis || analysis.status !== 'success') return null;
  const startMs = Date.parse(analysis.created_at);
  if (!Number.isFinite(startMs)) return null;
  const endMs = startMs + DEFAULT_THROTTLE_MINUTES * 60_000;
  return new Date(endMs).toISOString();
}

/**
 * Minutes until the given ISO timestamp, rounded UP so the operator
 * never sees "0 min" while the throttle is still active. Returns 0
 * when the timestamp has elapsed.
 */
export function minutesUntil(iso: string): number {
  const target = Date.parse(iso);
  if (!Number.isFinite(target)) return 0;
  const remainingMs = target - Date.now();
  if (remainingMs <= 0) return 0;
  return Math.ceil(remainingMs / 60_000);
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.kind === 'unauthorized') return 'Auth failed.';
    if (err.kind === 'not_found') return 'Thread no longer exists.';
    if (err.kind === 'network') return 'API unreachable.';
    if (err.kind === 'conflict') {
      // 409 from /analyze is always a force-mode mismatch — an existing
      // job is in flight that was queued with the other `force` value.
      // Surface a specific message so the operator knows to wait, not
      // panic.
      return 'Analysis already in progress with different settings — wait for it to finish, then retry.';
    }
    return `Request failed (${err.status}).`;
  }
  return err instanceof Error ? err.message : 'Unknown error.';
}
